import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Eye } from "lucide-react";

import { DataTableNew } from "@/components/shared/DataTable";
import { Card } from "@/components/ui/Card";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import FeedbackViewModal from "@/components/feedback/FeedbackViewModal";
import Skeleton from "@/components/shared/Skeleton";
import { Filter, FilterPill, FilterSelect } from "@/components/shared/Filter";
import { FEEDBACK_STATUS_LABEL, FEEDBACK_STATUS_OPTIONS, FEEDBACK_TYPE_LABEL, FEEDBACK_TYPE_OPTIONS } from "@/config/dropdownData";
import { FEEDBACK_COLUMNS, SENT_BY_COLUMN } from "@/components/feedback/feedbackColumns";
import { FeedbackService } from "@/services/feedbackService";
import useLiveResource from "@/hooks/useLiveResource";

const [SENT, ...REST] = FEEDBACK_COLUMNS;

// A member reads back their own feedback; a tenant admin reads the whole
// organisation's, each with who sent it. The server decides whose rows each
// call returns: GET /api/v2/feedback/mine, or /org for the tenant admin alone.
const VIEWS = {
  member: {
    title: "My Feedback",
    api: FeedbackService.mine,
    columns: FEEDBACK_COLUMNS,
    empty: "You haven't sent any feedback yet. Use the feedback button at the top of any page.",
  },
  admin: {
    title: "Feedback",
    api: FeedbackService.org,
    columns: [SENT, SENT_BY_COLUMN, ...REST],
    empty: "Nobody in your organisation has sent feedback yet.",
  },
};

const MyFeedback = () => {
  const setHeader = useOutletContext();
  const isTenantAdmin = useSelector((state) => state.auth?.user?.isTenantAdmin);
  const view = isTenantAdmin ? VIEWS.admin : VIEWS.member;
  const [tableData, setTableData] = useState({ total: 0, isLoading: false });
  const [reloadKey, setReloadKey] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewing, setViewing] = useState(null);

  // `feedback.changed` reaches this user's own connections: the platform team
  // moved one of theirs. The table re-reads its current page.
  useLiveResource({ keys: "feedback", onChange: () => setReloadKey((k) => k + 1) });

  useEffect(() => {
    setHeader({
      title: view.title,
      subTitle: tableData.isLoading ? <Skeleton className="h-5 w-28" /> : `${tableData.total} sent`,
    });
    return () => setHeader({ title: "", subTitle: "" });
  }, [tableData, setHeader, view]);

  const filters = [
    { key: "type", label: "All Types", value: typeFilter, onChange: setTypeFilter, options: FEEDBACK_TYPE_OPTIONS },
    { key: "status", label: "All Statuses", value: statusFilter, onChange: setStatusFilter, options: FEEDBACK_STATUS_OPTIONS },
  ];

  const activeFilters = [
    typeFilter && { key: "type", label: `Type: ${FEEDBACK_TYPE_LABEL[typeFilter]}`, clear: () => setTypeFilter("") },
    statusFilter && { key: "status", label: `Status: ${FEEDBACK_STATUS_LABEL[statusFilter]}`, clear: () => setStatusFilter("") },
  ].filter(Boolean);

  const columns = useMemo(
    () => [
      ...view.columns,
      {
        key: "id",
        label: "Action",
        sortable: false,
        align: "center",
        render: (_val, row) => (
          <Button variant="ghost" size="icon" aria-label="View feedback" title="View feedback" onClick={() => setViewing(row)} className="hover:bg-background-secondary">
            <Eye className="size-4" />
          </Button>
        ),
      },
    ],
    [view],
  );

  const extraParams = useMemo(() => ({ type: typeFilter || undefined, status: statusFilter || undefined }), [typeFilter, statusFilter]);

  return (
    <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
      <div className="flex items-center justify-end gap-2.5">
        <Filter
          filters={filters}
          activeFilters={activeFilters}
          showFilters={showFilters}
          onToggle={() => setShowFilters((p) => !p)}
          onClearAll={() => {
            setTypeFilter("");
            setStatusFilter("");
          }}
          renderFilter={(f) => <FilterSelect key={f.key} {...f} />}
          renderActiveFilter={(f) => <FilterPill key={f.key} {...f} />}
        />
      </div>

      <Card className="p-0 bg-table-heading border-border-primary">
        <DataTableNew
          key={isTenantAdmin ? "org-feedback" : "my-feedback"}
          className="rounded-none"
          columns={columns}
          api={view.api}
          sortable={false}
          dataPath="feedback"
          extraParams={extraParams}
          onDataFetch={setTableData}
          reloadKey={reloadKey}
          paginationLabel="feedback"
          emptyMessage={view.empty}
        />
      </Card>

      {viewing && <FeedbackViewModal feedback={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

export default MyFeedback;
